import React, { useState, useEffect, useRef } from 'react'
import { Document, Page } from 'react-pdf'
import { ChevronLeftIcon, ChevronRightIcon, Maximize2Icon, Minimize2Icon } from 'lucide-react'
import { IconDownload } from '@tabler/icons-react'
import { SecondaryButton } from 'src/components/atoms/buttons/SecondaryButton'
import Typography from 'src/components/atoms/Typography'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/atoms/dialog'
import 'src/enviroment/utils/pdfWorker'

interface PDFViewerModalProps {
  isOpen: boolean
  onClose: () => void
  pdfUrl: string | null
  fileName?: string
}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({ isOpen, onClose, pdfUrl, fileName = 'Document' }) => {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const [scale, setScale] = useState<number>(1)
  const pdfContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleDownload = () => {
    if (!pdfUrl) {
      return
    }
    const a = document.createElement('a')
    a.href = pdfUrl
    a.download = fileName
    a.target = '_blank'
    a.click()
  }

  // Reset state when PDF URL changes
  useEffect(() => {
    setNumPages(0)
    setPageNumber(1)
    setLoading(true)
    setError(null)
    setScale(1)
  }, [pdfUrl])

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
      setScale(document.fullscreenElement ? 1.5 : 1)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Adjust scale based on window size
  useEffect(() => {
    const handleResize = () => {
      if (!isFullscreen) {
        // Calculate appropriate scale based on window height
        // Adjust these values as needed for your specific design
        const windowHeight = window.innerHeight
        const maxContentHeight = windowHeight * 0.7 // 70% of window height

        // Get the content container height if available
        if (contentRef.current) {
          const contentHeight = contentRef.current.clientHeight
          if (contentHeight > maxContentHeight) {
            // Reduce scale to fit in the screen
            const newScale = Math.max(0.5, maxContentHeight / contentHeight)
            setScale(Math.min(newScale, 1)) // Don't go above 1
          } else {
            setScale(1) // Default scale
          }
        }
      }
    }

    if (isOpen && !loading) {
      // Add a small delay to ensure the document has rendered
      const timer = setTimeout(handleResize, 300)
      window.addEventListener('resize', handleResize)
      return () => {
        clearTimeout(timer)
        window.removeEventListener('resize', handleResize)
      }
    }
    return undefined
  }, [isOpen, loading, isFullscreen])

  if (!pdfUrl) return null

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
  }

  const onDocumentLoadError = (error: Error) => {
    setError(error.message)
    setLoading(false)
  }

  const goToPreviousPage = () => {
    setPageNumber((prevPage) => Math.max(prevPage - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prevPage) => Math.min(prevPage + 1, numPages))
  }

  const toggleFullscreen = async () => {
    if (!pdfContainerRef.current) return

    try {
      if (!isFullscreen) {
        await pdfContainerRef.current.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err)
    }
  }

  const renderPdfNavigation = () => (
    <div className="relative mt-4 flex items-center justify-center px-4 pb-2">
      <div className="flex items-center gap-4">
        <SecondaryButton size="sm" onClick={goToPreviousPage} disabled={pageNumber <= 1} leftIcon={<ChevronLeftIcon />}>
          Previous
        </SecondaryButton>
        <Typography variant="base" className="text-sm text-gray-100">
          Page {pageNumber} of {numPages}
        </Typography>
        <SecondaryButton
          size="sm"
          onClick={goToNextPage}
          disabled={pageNumber >= numPages}
          rightIcon={<ChevronRightIcon />}
        >
          Next
        </SecondaryButton>
      </div>
      <SecondaryButton onClick={toggleFullscreen} className="absolute right-4 h-8 w-8 p-0">
        {isFullscreen ? <Minimize2Icon size={16} /> : <Maximize2Icon size={16} />}
      </SecondaryButton>
    </div>
  )

  // Zoom controls
  const decreaseScale = () => setScale((prev) => Math.max(0.5, prev - 0.1))
  const increaseScale = () => setScale((prev) => Math.min(2, prev + 0.1))

  const renderZoomControls = () => (
    <div className="relative mt-2 flex items-center justify-center">
      <div className="flex items-center gap-2">
        <SecondaryButton onClick={decreaseScale} disabled={scale <= 0.5} className="h-8 w-8 p-0">
          <span className="text-lg">-</span>
        </SecondaryButton>
        <Typography variant="sm" className="w-20 text-center text-gray-100">
          {Math.round(scale * 100)}%
        </Typography>
        <SecondaryButton onClick={increaseScale} disabled={scale >= 2} className="h-8 w-8 p-0">
          <span className="text-lg">+</span>
        </SecondaryButton>
      </div>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-4xl">
        {/* the header is icon-only, so the file name is what names this dialog for a screen reader */}
        <DialogTitle className="sr-only">{fileName}</DialogTitle>
        <DialogHeader className="flex flex-row items-center justify-end space-y-0 pr-10">
          <button
            onClick={handleDownload}
            className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
            title="Download"
          >
            <div
              className="rounded-full"
              style={{ background: 'linear-gradient(to right, #329175, #EFEFCF 100%)', padding: '1px' }}
            >
              <div className="rounded-full bg-gray-900 p-1">
                <IconDownload className="h-4 w-4 text-gray-50" />
              </div>
            </div>
          </button>
        </DialogHeader>

        {error ? (
          <Typography variant="lg" className="text-center text-red-500">
            Error loading document: {error}
          </Typography>
        ) : (
          <div
            ref={pdfContainerRef}
            className="pdf-container flex-1 overflow-auto"
            style={{ maxHeight: isFullscreen ? '100vh' : 'calc(90vh - 160px)' }}
          >
            <div className="relative flex justify-center" ref={contentRef}>
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                className="flex justify-center"
                loading={
                  <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  className="shadow-lg"
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  scale={scale}
                />
              </Document>
            </div>
          </div>
        )}

        {!loading && (
          <div className="sticky bottom-0 pt-2 dark:bg-gray-900">
            {renderZoomControls()}
            {renderPdfNavigation()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PDFViewerModal
